import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Input from '@welldev-group/rcl-input';
import Button from '@welldev-group/rcl-button';
import Card from '@welldev-group/rcl-card';
import { Avatar, I18nText, EmphasisTag } from '@welldev-group/react-comp-lib';
import Popover from '@welldev-group/rcl-popover';
import LanguageSettings from '../../../shared/components/Language/LanguageSettings';
import AvatarModal from './AvatarModal';
import httpClient from '../../../shared/libraries/httpClient';
import routes from '../../../shared/routes';
import Channels from '../Channel';
import DateTime from '../DateTime';
import CancelScheduledDowngradeModule from '../Billing/Subcription/CancelScheduledDowngradeModule';

const Settings = props => {
  const [isEditing, setIsEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [locale, setLocale] = useState('');
  const [lastName, setLastName] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [error, setError] = useState({});
  const [legalName, setLegalName] = useState('');
  const [legalAddress, setLegalAddress] = useState('');
  const [generalError, setGeneralError] = useState({});
  const [isEditingGeneral, setIsEditingGeneral] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [is2faSetUp, setIs2faSetUp] = useState(false);
  const [passwordLastUpdatedAt, setPasswordLastUpdatedAt] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  const {
    canChangeSettings,
    canChangeChannels,
    currentLanguage,
    availableLocales,
    userId,
    affiliateId,
    scheduledDowngrade,
    urls,
  } = props;

  const generalParams = {
    affiliate: {
      client_name: legalName,
      client_address: legalAddress,
    },
  };

  const fetchSettings = async () => {
    const { data } = await httpClient.get(routes.settings.index());

    setFirstName(data.settings.firstName);
    setLocale(data.settings.locale);
    setLastName(data.settings.lastName);
    setAvatar(data.settings.avatar);
    setEmailAddress(data.settings.email);
    setLegalName(data.settings.clientName);
    setLegalAddress(data.settings.clientAddress);
    setIs2faSetUp(data.settings.isMultiFactorAuthenticationSetUp);
    setPasswordLastUpdatedAt(data.settings.passwordLastUpdatedAt);
  };

  const saveGeneralSettings = async () => {
    const { data } = await httpClient.put(
      routes.affiliates.update({
        id: affiliateId,
      }),
      generalParams,
    );

    if (data.error === null) {
      fetchSettings();
      setIsEditingGeneral(false);
    }
  };

  const onGeneralSettingsSave = () => {
    saveGeneralSettings().catch(({ response }) => {
      if (response.status === 422) {
        setGeneralError(response.data.error);
      }
    });
  };

  const onCloseGeneralSettings = () => {
    setGeneralError({});
    fetchSettings();
    setIsEditingGeneral(false);
  };

  const savePersonalSettings = async selectedLocale => {
    const params = {
      user: {
        first_name: firstName,
        last_name: lastName,
        email: emailAddress,
        locale: selectedLocale,
      },
    };

    const { data } = await httpClient.put(
      routes.users.update({
        id: userId,
      }),
      params,
    );

    if (data.error === null) {
      window.location.assign(data.settingsPath);
    }
  };

  const onPersonalSettingsSave = (selectedLocale = locale) => {
    savePersonalSettings(selectedLocale).catch(({ response }) => {
      if (response.status === 422) {
        setError(response.data.error);
      }
    });
  };

  const onClosePersonalSettings = () => {
    setError({});
    fetchSettings();
    setIsEditing(false);
  };

  const savePasswordSettings = async () => {
    const params = {
      user: {
        password,
      },
    };

    await httpClient.put(routes.users.updatePassword(), params);
  };

  const onPasswordSettingsSave = () => {
    savePasswordSettings().then(() => {
      setIsEditingPassword(false);
      setPassword('');
      setConfirmPassword('');
    });
  };

  const disable2fa = async () => {
    await httpClient.delete(routes.users.multiFactorAuth());
  };

  const onDisable2fa = () => {
    disable2fa().then(() => {
      fetchSettings();
    });
  };

  const enable2fa = async () => {
    await httpClient.put(routes.users.multiFactorAuth());
  };

  const onEnable2fa = () => {
    enable2fa().then(() => {
      fetchSettings();
      window.location.replace(urls.loginUrl);
    });
  };

  const onClosePasswordSettings = () => {
    setIsEditingPassword(false);
    setPassword('');
    setConfirmPassword('');
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return (
    <div className="settings__container">
      <div className="settings__avatar">
        <Avatar
          firstName={firstName}
          lastName={lastName}
          src={avatar}
          size="xl"
        />
        <div className="settings__avatar-change-icon">
          <AvatarModal userId={userId} />
        </div>
      </div>
      <div className="settings__header settings-header__welcome">
        <I18nText id="settings.welcome_header" />
      </div>
      <div className="settings__header settings-header__name">
        {firstName} {lastName}
      </div>
      <div className="settings__header settings-header__info">
        <I18nText id="settings.header_info" />
      </div>
      <div className="container--sm-full-width">
        <div className="grid">
          {canChangeSettings && (
            <div className="col-lg-4 col-md-4 col-12 settings--left-side-padding">
              <Card
                version="v2"
                title={
                  <div className="settings__sections-header">
                    <div className="settings__sections-header-title">
                      <I18nText id="settings.sections.general" />
                    </div>
                    <div className="settings__sections-header-actions">
                      {isEditingGeneral && (
                        <div className="settings__sections-header-cancelButton">
                          <Button
                            version="v2"
                            size="small"
                            onClick={onCloseGeneralSettings}
                            label={
                              <I18nText id="settings.sections.cancel_button" />
                            }
                          />
                        </div>
                      )}
                      <Button
                        version="v2"
                        size="small"
                        label={
                          isEditingGeneral ? (
                            <I18nText id="settings.sections.save_button" />
                          ) : (
                            <I18nText id="settings.sections.change_button" />
                          )
                        }
                        type="primary"
                        onClick={() =>
                          isEditingGeneral
                            ? onGeneralSettingsSave()
                            : setIsEditingGeneral(true)
                        }
                      />
                    </div>
                  </div>
                }
              >
                <div className="settings__sections-container">
                  <div className="settings__sections-sub-header">
                    <I18nText id="settings.sub_sections.legal_name" />
                  </div>
                  {isEditingGeneral ? (
                    <div className="settings__sections-input">
                      <Input
                        onChange={value => setLegalName(value)}
                        placeholder={I18n.t(
                          'components.settings.sub_sections.legal_name',
                        )}
                        value={legalName}
                        error={generalError.clientName}
                        touched={true}
                        isClearable={false}
                      />
                    </div>
                  ) : (
                    <div>{legalName}</div>
                  )}
                  <div className="settings__sections-sub-header settings__sections-sub-header--with-padding">
                    <I18nText id="settings.sub_sections.legal_address" />
                  </div>
                  {isEditingGeneral ? (
                    <div className="settings__sections-input">
                      {' '}
                      <Input
                        onChange={value => setLegalAddress(value)}
                        placeholder={I18n.t(
                          'components.settings.sub_sections.legal_address',
                        )}
                        value={legalAddress}
                        error={generalError.clientAddress}
                        touched={true}
                        isClearable={false}
                      />
                    </div>
                  ) : (
                    <div>{legalAddress}</div>
                  )}
                </div>
              </Card>
            </div>
          )}
          <div className="col-lg-4 col-md-4 col-12 settings--general-padding">
            <Card
              version="v2"
              title={
                <div className="settings__sections-header">
                  <div className="settings__sections-header-title">
                    <I18nText id="settings.sections.personal" />
                  </div>
                  <div className="settings__sections-header">
                    {isEditing && (
                      <div className="settings__sections-header-cancelButton">
                        <Button
                          version="v2"
                          size="small"
                          onClick={onClosePersonalSettings}
                          label={
                            <I18nText id="settings.sections.cancel_button" />
                          }
                        />
                      </div>
                    )}
                    <Button
                      version="v2"
                      size="small"
                      label={
                        isEditing ? (
                          <I18nText id="settings.sections.save_button" />
                        ) : (
                          <I18nText id="settings.sections.change_button" />
                        )
                      }
                      type="primary"
                      onClick={() =>
                        isEditing
                          ? onPersonalSettingsSave()
                          : setIsEditing(true)
                      }
                    />
                  </div>
                </div>
              }
            >
              <div className="settings__sections-container">
                <div className="settings__sections-sub-header">
                  <I18nText id="settings.sub_sections.name" />
                </div>
                {isEditing ? (
                  <>
                    <div className="settings__sections-input">
                      <Input
                        onChange={value => setFirstName(value)}
                        placeholder={I18n.t(
                          'components.settings.sub_sections.first_name',
                        )}
                        value={firstName}
                        error={error.firstName}
                        touched={true}
                        isClearable={false}
                      />
                    </div>
                    <div className="settings__sections-input">
                      <Input
                        onChange={value => setLastName(value)}
                        placeholder={I18n.t(
                          'components.settings.sub_sections.last_name',
                        )}
                        value={lastName}
                        error={error.lastName}
                        touched={true}
                        isClearable={false}
                      />
                    </div>
                  </>
                ) : (
                  <div>{`${firstName} ${lastName}`}</div>
                )}
                <div className="settings__sections-sub-header settings__sections-sub-header--with-padding">
                  <I18nText id="settings.sub_sections.email" />
                </div>
                {isEditing ? (
                  <div className="settings__sections-input">
                    <Input
                      onChange={value => setEmailAddress(value)}
                      placeholder={I18n.t(
                        'components.settings.sub_sections.email',
                      )}
                      value={emailAddress}
                      error={error.email}
                      touched={true}
                      isClearable={false}
                    />
                  </div>
                ) : (
                  <div>{emailAddress}</div>
                )}
              </div>
            </Card>
          </div>

          <div className="col-lg-4 col-md-4 col-12 col-bleed settings--right-side-padding settings__language-card">
            <Card
              version="v2"
              title={
                <div className="settings__sections-header">
                  <div className="settings__sections-header-title">
                    <I18nText id="settings.sections.language" />
                  </div>
                  <Popover
                    className="settings__sections-header-locale-wrapper"
                    size="small"
                    isVisible={isVisible}
                    onOutsideClick={() => setIsVisible(false)}
                    content={
                      <LanguageSettings
                        availableLocales={availableLocales}
                        saveLocale={value => onPersonalSettingsSave(value)}
                      />
                    }
                  >
                    <Button
                      label={<I18nText id="settings.sections.change_button" />}
                      type="primary"
                      size="small"
                      onClick={() => {
                        setIsVisible(
                          previousIsVisibleState => !previousIsVisibleState,
                        );
                      }}
                    />
                  </Popover>
                </div>
              }
            >
              <div className="settings__sections-container">
                <div className="settings__sections-sub-header">
                  <I18nText id="settings.sub_sections.current_language" />
                </div>
                <div className="settings__sections-container-language-container">
                  {currentLanguage}
                </div>
              </div>
            </Card>
          </div>
          <div className="col-lg-4 col-md-4 col-12 settings--left-side-padding">
            <div className="col-grid col-bleed">
              <div className="settings__password-card">
                <Card
                  version="v2"
                  title={
                    <div className="settings__sections-header">
                      <div className="settings__sections-header-title">
                        <I18nText id="settings.sections.password" />
                      </div>
                      <div className="settings__sections-header">
                        {isEditingPassword && (
                          <div className="settings__sections-header-cancelButton">
                            <Button
                              version="v2"
                              size="small"
                              onClick={onClosePasswordSettings}
                              label={
                                <I18nText id="settings.sections.cancel_button" />
                              }
                            />
                          </div>
                        )}
                        <Button
                          version="v2"
                          size="small"
                          label={
                            isEditingPassword ? (
                              <I18nText id="settings.sections.save_button" />
                            ) : (
                              <I18nText id="settings.sections.change_button" />
                            )
                          }
                          disabled={
                            isEditingPassword &&
                            (password !== confirmPassword || !password)
                          }
                          type="primary"
                          onClick={() =>
                            isEditingPassword
                              ? onPasswordSettingsSave()
                              : setIsEditingPassword(true)
                          }
                        />
                      </div>
                    </div>
                  }
                >
                  <div className="settings__sections-container">
                    {!isEditingPassword && (
                      <div className="settings__sections-sub-header">
                        <I18nText id="settings.sub_sections.password" />
                      </div>
                    )}
                    {isEditingPassword ? (
                      <>
                        <div className="settings__sections-input">
                          <Input
                            onChange={value => setPassword(value)}
                            placeholder={I18n.t(
                              'components.settings.placeholders.password',
                            )}
                            type="password"
                            value={password}
                            touched={true}
                          />
                        </div>
                        <div className="settings__sections-input">
                          <Input
                            onChange={value => setConfirmPassword(value)}
                            placeholder={I18n.t(
                              'components.settings.placeholders.confirm_password',
                            )}
                            type="password"
                            value={confirmPassword}
                            error={
                              password !== confirmPassword &&
                              I18n.t(
                                'components.settings.error.confirm_password',
                              )
                            }
                            touched={true}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="col-grid direction-row col-bleed align-center justify-space-between">
                        <div className="col-grid col-bleed">
                          <span>*********</span>
                          {passwordLastUpdatedAt && (
                            <span>
                              {I18n.t(
                                'components.settings.sub_sections.last_updated',
                              )}{' '}
                              {' - '}
                              <DateTime
                                dateTime={passwordLastUpdatedAt}
                                format="short"
                              />
                            </span>
                          )}
                          <div className="settings__password-card-two-fa-button">
                            <div className="col-grid direction-row col-bleed justify-space-between align-center">
                              <I18nText id="settings.sections.configure_2fa" />
                              <Button
                                version="v2"
                                size="small"
                                onClick={
                                  is2faSetUp ? onDisable2fa : onEnable2fa
                                }
                                label={
                                  is2faSetUp ? (
                                    <I18nText id="settings.sub_sections.disable_2fa" />
                                  ) : (
                                    <I18nText id="settings.sub_sections.configure_2fa" />
                                  )
                                }
                                type="primary"
                              />
                            </div>
                          </div>
                          <div>
                            <div className="settings__sections-sub-header">
                              <I18nText id="settings.sub_sections.current_status" />
                            </div>
                            {is2faSetUp ? (
                              <EmphasisTag
                                type="success"
                                text={
                                  <I18nText id="settings.sub_sections.enabled" />
                                }
                                size="tiny"
                              />
                            ) : (
                              <EmphasisTag
                                type="disabled"
                                text={
                                  <I18nText id="settings.sub_sections.disabled" />
                                }
                                size="tiny"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </div>
          </div>
          {!props.signedInAsSuperAdmin && canChangeChannels && (
            <div className="col-lg-8 col-md-8 col-12 settings--right-side-padding">
              {scheduledDowngrade ? (
                <div className="grid">
                  <div className="col-12">
                    <CancelScheduledDowngradeModule
                      affiliateId={affiliateId}
                      scheduledDowngrade={scheduledDowngrade}
                      redirectTo={urls.settings}
                    />
                  </div>
                </div>
              ) : null}
              <div className="grid">
                <div className="col-12">
                  <Channels
                    affiliateId={affiliateId}
                    rowLevelActionDisabled={Boolean(scheduledDowngrade)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Settings.defaultProps = {
  canChangeSettings: false,
  canChangeChannels: false,
  scheduledDowngrade: null,
  urls: {
    settings: '',
  },
};

Settings.propTypes = {
  affiliateId: PropTypes.number.isRequired,
  canChangeSettings: PropTypes.bool,
  canChangeChannels: PropTypes.bool,
  currentLanguage: PropTypes.string.isRequired,
  availableLocales: PropTypes.shape({
    code: PropTypes.string.isRequired,
    localeUrl: PropTypes.string.isRequired,
    humanName: PropTypes.string.isRequired,
  }).isRequired,
  userId: PropTypes.number.isRequired,
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
  scheduledDowngrade: PropTypes.shape({
    id: PropTypes.number,
    plan: PropTypes.shape({}),
    downgradedFeatures: {
      lessWorkspaces: PropTypes.number,
      lessChannels: PropTypes.number,
      customDomainNotSupported: PropTypes.bool,
      lessPhoneSupport: PropTypes.bool,
    },
    downgradeAt: PropTypes.string,
  }),
  urls: PropTypes.shape({
    settings: PropTypes.string.isRequired,
    loginUrl: PropTypes.string.isRequired,
  }),
};

export default Settings;
